/**
 * The API reports every rejected payload under the single code
 * `error.validation.failed`, so translating the code alone yields "Validation
 * error" — a toast that names neither the field nor the reason.
 *
 * The payload always carries more than that. Two shapes exist:
 *
 * - `errors`: a per-field map (`{ 'sstEvents.0.orbit.epoch': ['...'] }`), sent
 *   when the backend rethrows the Joi error object itself.
 * - `message`: Joi's flattened sentence
 *   (`"sstEvents[0].orbit.epoch" must be a valid date. "critical" is required`),
 *   sent by the `validateBody` middleware, which keeps only the message.
 *
 * This module turns either shape into lines the user can act on: the JSON path
 * becomes the label printed next to the input, and the Joi reason becomes the
 * matching form error message when we have a translation for it.
 */

type TranslationFunction = (key: string, options?: Record<string, any>) => string;

/** Payload leaf → translation key of the label rendered above the input. */
const FIELD_LABEL_KEYS: Record<string, string> = {
  orbitCategory: 'sstEvent.orbitCategory',
  apogee: 'orbit.apogee',
  perigee: 'orbit.perigee',
  inclination: 'orbit.inclination',
  accuracy: 'sstEvent.accuracy',
  raan: 'orbit.raan',
  perigeeArgument: 'orbit.perigeeArgument',
  epoch: 'orbit.epoch',
  meanAnomalyAtEpoch: 'orbit.meanAnomalyAtEpoch',
  effectiveAreaMassRatio: 'orbit.effectiveAreaMassRatio',
  operationType: 'sstEvent.operationType',
  operationDate: 'sstEvent.operationDate',
  operationalStatus: 'sstEvent.operationalStatus',
  communicationStatus: 'sstEvent.communicationStatus',
  orbitalStatus: 'sstEvent.orbitalStatus',
  attitudeStatus: 'sstEvent.attitudeStatus',
};

/**
 * Joi reason → existing form error key, so the line reads in the user's
 * language. A bound carries its limit into the message (`{{min}}`, `{{max}}`),
 * read off the reason itself: the API is the only place the limit is written.
 * The apogee's own bound is a reference to the perigee, not a number, and gets
 * its own sentence.
 */
const REASON_KEYS: Array<{ match: RegExp; key: string; values?: (match: RegExpMatchArray) => Record<string, string> }> =
  [
    { match: /must be a valid date$/, key: 'form.errors.format-date' },
    { match: /(is required|is not allowed to be empty)$/, key: 'form.errors.required' },
    { match: /must be a number$/, key: 'form.errors.format-number' },
    { match: /must be greater than or equal to ref:perigee$/, key: 'form.errors.apogeeBelowPerigee' },
    { match: /must be greater than or equal to (-?[\d.]+)$/, key: 'form.errors.min', values: ([, min]) => ({ min }) },
    { match: /must be less than or equal to (-?[\d.]+)$/, key: 'form.errors.max', values: ([, max]) => ({ max }) },
  ];

/** `sstEvents[0].orbit.epoch` and `sstEvents.0.orbit.epoch` both leaf to `epoch`. */
const leafOf = (path: string): string => {
  const segments = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(segment => segment !== '' && !/^\d+$/.test(segment));
  return segments[segments.length - 1] ?? path;
};

const translateOrRaw = (
  key: string | undefined,
  raw: string,
  t: TranslationFunction,
  values?: Record<string, string>,
): string => {
  if (!key) {
    return raw;
  }
  const translated = t(key, values);
  return translated === key ? raw : translated;
};

const validationFieldLabel = (path: string, t: TranslationFunction): string => {
  const leaf = leafOf(path);
  return translateOrRaw(FIELD_LABEL_KEYS[leaf], leaf, t);
};

const validationReason = (reason: string, t: TranslationFunction): string => {
  for (const { match, key, values } of REASON_KEYS) {
    const matched = reason.match(match);
    if (matched) {
      return translateOrRaw(key, reason, t, values?.(matched));
    }
  }
  return reason;
};

/**
 * Joi concatenates its details as `"<path>" <reason>. "<path>" <reason>`.
 * Anything that does not follow that shape yields no entry, and the caller
 * falls back to the raw message.
 */
const JOI_DETAIL = /"([^"]+)"\s+([^"]*?)(?=\.\s+"|\s*$)/g;

export const parseJoiValidationMessage = (message: string): Array<{ path: string; reason: string }> => {
  const details: Array<{ path: string; reason: string }> = [];
  // `matchAll` returns an iterator the current TS target cannot spread.
  const pattern = new RegExp(JOI_DETAIL.source, 'g');
  let match = pattern.exec(message);
  while (match !== null) {
    const [, path, reason] = match;
    if (reason.trim()) {
      details.push({ path, reason: reason.trim() });
    }
    match = pattern.exec(message);
  }
  return details;
};

/**
 * Builds the toast body for `error.validation.failed`, one line per rejected
 * field. Returns undefined when the payload carries nothing usable — the caller
 * then keeps the generic translated message.
 */
export const describeValidationError = (
  { message, fieldErrors }: { message?: string; fieldErrors?: Record<string, string[]> },
  t: TranslationFunction,
): string | undefined => {
  const lines = Object.entries(fieldErrors ?? {}).flatMap(([path, messages]) =>
    (Array.isArray(messages) ? messages : []).map(reason => ({ path, reason })),
  );

  if (lines.length === 0 && message) {
    lines.push(...parseJoiValidationMessage(message));
  }

  if (lines.length === 0) {
    return undefined;
  }

  return lines.map(({ path, reason }) => `${validationFieldLabel(path, t)}: ${validationReason(reason, t)}`).join('\n');
};
